"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Server, Palette } from "lucide-react";
const DigitalNetwork = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    canvas.width = width;
    canvas.height = height;
    const nodes = Array.from({
      length: 40
    }, (_, i) => ({
      id: i,
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 2,
      r: Math.random() * 4 + 2,
      group: Math.random() > 0.5 ? "host" : "design"
    }));
    const links: {
      source: typeof nodes[0];
      target: typeof nodes[0];
    }[] = [];
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        if (Math.random() > 0.85) {
          links.push({
            source: nodes[i],
            target: nodes[j]
          });
        }
      }
    }
    const animate = () => {
      ctx.fillStyle = "rgba(0, 0, 0, 0.05)";
      ctx.fillRect(0, 0, width, height);
      nodes.forEach(node => {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x - node.r < 0 || node.x + node.r > width) {
          node.vx *= -1;
          node.x = Math.max(node.r, Math.min(width - node.r, node.x));
        }
        if (node.y - node.r < 0 || node.y + node.r > height) {
          node.vy *= -1;
          node.y = Math.max(node.r, Math.min(height - node.r, node.y));
        }
        node.vx *= 0.99;
        node.vy *= 0.99;
      });
      ctx.strokeStyle = "rgba(0, 255, 255, 0.2)";
      ctx.lineWidth = 1;
      links.forEach(link => {
        ctx.beginPath();
        ctx.moveTo(link.source.x, link.source.y);
        ctx.lineTo(link.target.x, link.target.y);
        ctx.stroke();
      });
      nodes.forEach(node => {
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.r);
        if (node.group === "host") {
          gradient.addColorStop(0, "rgba(0, 255, 255, 0.8)");
          gradient.addColorStop(1, "rgba(0, 255, 255, 0.2)");
        } else {
          gradient.addColorStop(0, "rgba(255, 0, 255, 0.8)");
          gradient.addColorStop(1, "rgba(255, 0, 255, 0.2)");
        }
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.r, 0, Math.PI * 2);
        ctx.fill();
      });
      animationRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  return <div data-section-id="26490" ref={containerRef} className="w-full h-full min-h-[400px] relative overflow-hidden flex items-center justify-center">
      <canvas ref={canvasRef} className="w-full h-full absolute inset-0 pointer-events-auto" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-primary)_0%,transparent_70%)] opacity-10 blur-3xl pointer-events-none" />
    </div>;
};
export default function TheLaunchpad() {
  return <section id="architecting-digital-sovereignty" className="dark bg-background text-foreground relative w-full min-h-[90vh] flex items-center overflow-hidden py-12 lg:py-0">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(190_90%_15%)_0%,transparent_50%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,hsl(222_47%_4%)_0%,transparent_50%)] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 h-full">
        <div className="flex flex-col lg:flex-row items-center h-full gap-12 lg:gap-8">
          
          <div className="w-full lg:w-[60%] flex flex-col justify-center space-y-8 text-center lg:text-left">
            
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-default font-medium w-fit mx-auto lg:mx-0 backdrop-blur-sm">
              <Sparkles className="w-4 h-4" />
              <span>Model Control Protocol</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-default font-extrabold tracking-tighter leading-[1.1]">
              <span className="text-foreground drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]">Applied</span>
              <br />
              <span className="bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent drop-shadow-[0_0_20px_rgba(0,255,255,0.4)]">Intelligence.</span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground font-default max-w-2xl mx-auto lg:mx-0 leading-relaxed">TidalSwing AI engineers high-performance computing infrastructure and next-generation applications. We build scalable digital ecosystems combining enterprise-grade execution with visionary product development.</p>

            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start pt-4">
              <Button asChild size="lg" className="h-14 px-8 text-lg rounded-sm bg-primary text-primary-foreground font-default hover:bg-primary/90 shadow-[0_0_20px_rgba(0,255,255,0.4)] transition-all hover:scale-105">
                <Link to="#">
                  <Server className="mr-2 h-5 w-5" />
                  Explore Ecosystem
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="h-14 px-8 text-lg rounded-sm border-secondary text-secondary font-default hover:bg-secondary/10 hover:text-secondary hover:border-secondary transition-all hover:scale-105 backdrop-blur-sm">
                <Link to="#">
                  <Palette className="mr-2 h-5 w-5" />
                  Our Insights
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            <div className="pt-8 flex items-center justify-center lg:justify-start gap-8 text-sm text-muted-foreground/60 font-mono">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
                <span>Apple Business Partner</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse delay-75" />
                <span>Salesforce SI Partner</span>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-[40%] h-[400px] lg:h-[600px] relative flex items-center justify-center">
            <div className="relative w-full h-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-md shadow-2xl overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10 opacity-50" />
              
              <DigitalNetwork />

              <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end pointer-events-none">
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Node Status</div>
                  <div className="text-xs font-mono text-primary">● Sovereign</div>
                </div>
                <div className="text-[10px] font-mono text-white/20">
                  TS-AI-V2.0
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}